import React from "react";
import { SxStyleProp } from "theme-ui";
import { useTransition, animated } from "react-spring";
import { ThickArrowIcon } from "../icons";
import Box from "../Box/Box";
const AnimatedBox = animated(Box);

const DrawerContext = React.createContext<DrawerContext>({});
const useDrawerContext = () => React.useContext(DrawerContext);

const sizes = {
  sm: "24rem",
  md: "36rem",
  lg: "48rem",
  xl: "64rem",
  full: "100%",
};

interface DrawerContext {
  isOpen?: boolean;
  onClose?: (event: any) => void;
  onOpen?: (event: any) => void;
  onToggle?: (event: any) => void;
  size?: keyof typeof sizes;
}

interface Props extends DrawerContext {
  sx?: SxStyleProp;
}

export const Drawer: React.FunctionComponent<Props> = ({
  isOpen,
  onClose,
  onOpen,
  onToggle,
  size = "lg",
  children,
}) => (
  <DrawerContext.Provider
    value={{
      isOpen,
      onClose,
      onOpen,
      onToggle,
      size,
    }}
  >
    {/* {isOpen ? <Box {...restProps}>{children}</Box> : null} */}
    {children}
  </DrawerContext.Provider>
);

export const DrawerContent: React.FunctionComponent<{ sx?: SxStyleProp }> = ({
  sx,
  ...restProps
}) => {
  const { isOpen, size } = useDrawerContext();
  const transitions = useTransition(isOpen, null, {
    from: { transform: "translate3d(100%, 0, 0)" },
    enter: { transform: "translate3d(0, 0, 0)" },
    leave: { transform: "translate3d(100%, 0, 0)" },
    config: { duration: 0 },
  });

  return (
    <React.Fragment>
      {transitions.map(
        ({ item, key, props }) =>
          item && (
            <AnimatedBox
              key={key}
              style={props}
              sx={{
                position: "fixed",
                top: 0,
                right: 0,
                width: sizes[size!],
                maxWidth: "100%",
                height: "100%",
                bg: "white",
                zIndex: "drawer",
                borderLeft: "1px solid #000",
                overflow: "auto",
                ...(sx as any),
              }}
              {...restProps}
            />
          ),
      )}
    </React.Fragment>
  );
};

export const DrawerOverlay: React.FunctionComponent<{ sx?: SxStyleProp }> = ({
  sx,
  ...restProps
}) => {
  const { isOpen, onClose } = useDrawerContext();
  const transitions = useTransition(isOpen, null, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 150 },
  });

  return (
    <React.Fragment>
      {transitions.map(
        ({ item, key, props }) =>
          item && (
            <AnimatedBox
              key={key}
              style={props}
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                bg: "rgba(0,0,0,0.4)",
                zIndex: "overlay",
                ...(sx as any),
              }}
              onClick={onClose}
              {...restProps}
            />
          ),
      )}
    </React.Fragment>
  );
};

export const DrawerToggleButton: React.FunctionComponent<{
  sx?: SxStyleProp;
}> = ({ sx, ...restProps }) => {
  const { isOpen, onToggle, size } = useDrawerContext();
  // const { x, opacity }: any = useSpring({
  //   from: { x: 0, opacity: 1 },
  //   to: async (next: any) => {
  //     if (isOpen) {
  //       await next({ opacity: 0, config: { duration: 50 } });
  //       await delay(200);
  //       await next({
  //         x: Number(sizes[size!].replace("rem", "")),
  //       });
  //       await next({ opacity: 1 });
  //     } else {
  //       next({ opacity: 0, config: { duration: 0 } });
  //       await next({ x: 0 });
  //       await delay(250);
  //       await next({ opacity: 1 });
  //     }
  //   },
  // });

  return (
    <AnimatedBox
      onClick={onToggle}
      // style={{ right: x.interpolate((v: any) => `${v}rem`), opacity }}
      style={{ right: isOpen ? sizes[size!] : 0 }}
      sx={{
        position: "fixed",
        top: "100px",
        right: "0",
        cursor: "pointer",
        bg: "white",
        display: "flex",
        alignItems: "center",
        border: "1px solid #000",
        borderRadius: "full",
        width: "5rem",
        padding: "0.5rem 1rem",
        marginRight: "-1.5rem",
        zIndex: "drawer",

        "&:after": {
          content: "''",
          position: "absolute",
          bg: "white",
          width: "1.5rem",
          height: "calc(100% + 2px)",
          right: "-2px",
        },

        ...(sx as any),
      }}
      {...restProps}
    >
      <ThickArrowIcon direction={isOpen ? "right" : "left"} size="28px" />
    </AnimatedBox>
  );
};
